import cx from 'classnames'
import { type Observable } from 'rxjs'
import {
  type Ref,
  type ReactNode,
  useRef,
  type PointerEventHandler,
  useCallback,
  type AriaAttributes,
  useState,
} from 'react'
import type {
  FormControlProps,
  KeyboardEventsProps,
  MouseEventsProps,
} from '@/infra/prop-types'
import styles from './TextField.module.css'
import { composeRefs } from '@radix-ui/react-compose-refs'
import {
  type InputShellInheritedProps,
  InputShellControl,
  InputShellRoot,
  InputShellSlot,
} from '@/components/inputs/InputShell'
import { mergeProps, useFocus } from 'react-aria'
import { useIsSkeleton } from '@/components/Skeleton'
import { useObservable } from '@/hooks/rx-hooks'
import { useInputMask } from '@/hooks/input-mask'
import type { MaskBlock } from '@/utils/input-mask-util'

export interface TextFieldProps
  extends KeyboardEventsProps<HTMLInputElement>,
    MouseEventsProps<HTMLInputElement>,
    InputShellInheritedProps,
    FormControlProps,
    AriaAttributes {
  /**
   * Can be used to render slots.
   */
  children?: ReactNode
  /**
   * Whether the input is read-only.
   */
  readOnly?: boolean
  /**
   * Whether the input is disabled.
   */
  disabled?: boolean
  /**
   * Indicates that the input is required.
   */
  required?: boolean
  /**
   * Whether a skeleton overlay should be rendered.
   * Also disables the input.
   */
  isSkeleton?: boolean
  /**
   * Input placeholder.
   */
  placeholder?: string
  /**
   * Optional ref.
   */
  ref?: Ref<HTMLInputElement>
  /**
   * The current value of the input.
   */
  value?: string | undefined
  /**
   * The initial value for the input.
   */
  defaultValue?: string
  /**
   * Title attribute.
   */
  title?: string
  /**
   * Autocomplete attribute.
   */
  autoComplete?: AutoFill
  /**
   * @see HTMLInputElement.inert
   */
  inert?: boolean
  /**
   * Whether the input should receive focus on render.
   */
  autoFocus?: boolean
  /**
   * Focus change handler.
   */
  onFocusChange?: (isFocused: boolean) => unknown
  /**
   * When an item is emitted, focus is triggered on the input.
   * If `true`, prevents scrolling.
   */
  focus$?: Observable<boolean>
  /**
   * Input type.
   */
  type?:
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'hidden'
    | 'month'
    | 'number'
    | 'password'
    | 'search'
    | 'tel'
    | 'text'
    | 'time'
    | 'url'
    | 'week'
  /**
   * Change handler.
   *
   * @param value
   */
  onChange?: (newValue: string) => unknown
  /**
   * Optional input mask.
   */
  mask?: MaskBlock[]
  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Text input root element.
 */
export function TextField({
  ref,
  invalid,
  children,
  disabled,
  isSkeleton,
  variant,
  inert,
  onFocusChange,
  focus$,
  value: propValue,
  defaultValue,
  onChange: propOnChange,
  mask,
  testId,
  ...rest
}: TextFieldProps) {
  const isControlled = propValue !== undefined
  const [internalValue, setInternalValue] = useState(defaultValue ?? '')
  const effectiveValue = propValue ?? internalValue
  const changeValue = useCallback(
    (newValue: string) => {
      if (!isControlled) {
        setInternalValue(newValue)
      }

      propOnChange?.(newValue)
    },
    [isControlled, propOnChange],
  )
  const { maskedValue, onChange } = useInputMask({
    mask,
    onChange: changeValue,
    value: effectiveValue,
  })

  isSkeleton = useIsSkeleton(isSkeleton)
  disabled ||= isSkeleton
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the input element when any part of the component receives a
  // pointer-down event, unless the target was an interactive element.
  const focusInputOnClick: PointerEventHandler = useCallback((event) => {
    const target = event.target as HTMLElement
    if (target.closest('input, button, a')) {
      return
    }

    const input = inputRef.current
    if (!input) {
      return
    }

    // Check whether the event target was a right-sided slot.
    const isRightSlot = !!target.closest(`[data-slot-side=right]`)

    const cursorPosition = isRightSlot ? input.value.length : 0

    requestAnimationFrame(() => {
      // Only some input types support this, browsers will throw an error if not supported
      // See: https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange#:~:text=Note%20that%20according,not%20support%20selection%22.
      try {
        input.setSelectionRange(cursorPosition, cursorPosition)
      } catch (_e) {
        /* empty */
      }
      input.focus()
    })
  }, [])

  const [isFocused, setFocused] = useState(false)
  const focusChange = useCallback(
    (isFocused: boolean) => {
      setFocused(isFocused)
      onFocusChange?.(isFocused)
    },
    [onFocusChange],
  )
  const { focusProps } = useFocus({
    isDisabled: disabled,
    onFocusChange: focusChange,
  })

  // Trigger focus when observable emits
  useObservable(focus$, (preventScroll: boolean) => {
    inputRef.current?.focus({ preventScroll })
  })

  return (
    <InputShellRoot
      isTextField
      invalid={invalid}
      isDisabled={disabled}
      isFocused={isFocused}
      onPointerDown={focusInputOnClick}
      isSkeleton={isSkeleton}
      variant={variant}
    >
      <InputShellControl>
        <input
          ref={composeRefs(ref, inputRef)}
          type="text"
          disabled={disabled}
          inert={inert}
          data-test={testId}
          {...mergeProps(focusProps, rest)}
          value={maskedValue}
          onChange={(e) => onChange(e.target.value)}
          className={cx(styles.input)}
        />
      </InputShellControl>
      {children}
    </InputShellRoot>
  )
}

export { InputShellSlot as TextFieldSlot }
